/**
 * Replace every [Ref N] token in a narrative string with an inline
 * <RefLink/>, leaving everything else as plain text. The split regex
 * has a capturing group so the splitter retains both the surrounding
 * text and the matched tokens — concatenating the result reproduces
 * the original string.
 *
 * R-70 — also renders doc:// provenance URLs as links to /docs/:docId.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import {
  normalizeCompoundRefTokens,
  stripUnresolvableRefTokens,
  removeOutOfBoundsRefTokens,
  ensureSpaceAfterRefTokens,
} from './citationTokens';
import type { DataSource, SiteDocument } from '../types/siteRag';
import type { SiteEvidenceDocument } from '../types/siteEvidence';
import { RefLink } from '../components/siterag/report/RefLink';
import { WithheldNarrativeNotice } from '../components/siterag/report/WithheldNarrativeNotice';
import { isWithheldNarrativeNotice } from './reportRendering/scrubInternalSentinels';
import { resolveDocumentLineage } from './resolveDocumentLineage';
import { parseDocCitationUrl } from './audit/citationIntegrityVerifier';
import { stripNarrativeArtifacts } from './reportRendering/narrativeArtifacts';
import { presentReportReliabilityTokens } from './reportRendering/presentReportReliabilityTokens';
// Pure string cleanup, shared with the DOCX/PPTX exporters; re-exported here.
export { stripNarrativeArtifacts };

const SPLIT_RE = /(\[Ref \d+\])/g;
const TOKEN_RE = /^\[Ref (\d+)\]$/;
const DOC_SPLIT_RE = /(doc:\/\/[^\s)\]"']+)/gi;

function DocCitationLink({ url, docs }: { url: string; docs?: SiteDocument[] }) {
  const anchor = parseDocCitationUrl(url);
  if (!anchor) return <span>{url}</span>;
  if (anchor.docId.startsWith('sim/')) {
    return (
      <span
        className="text-text-muted italic"
        title="This report used the retired BoP execution system and must be regenerated."
        data-testid="retired-sim-citation"
      >
        legacy simulation reference retired
      </span>
    );
  }
  const doc = docs?.find((d) => d.id === anchor.docId);
  const label = doc?.fileName ?? anchor.docId;
  const params = new URLSearchParams();
  if (anchor.page > 1) params.set('page', String(anchor.page));
  if (anchor.charEnd > anchor.charStart) {
    params.set('highlight', `${anchor.charStart},${anchor.charEnd}`);
  }
  const qs = params.toString();
  const href = `/docs/${encodeURIComponent(anchor.docId)}${qs ? `?${qs}` : ''}`;
  return (
    <Link
      to={href}
      className="text-primary underline underline-offset-2 font-medium"
      title={`View source: ${label} (page ${anchor.page})`}
      data-testid="doc-citation-link"
    >
      {label}
    </Link>
  );
}

/** Split a text fragment into [head, trailingWord]: the last run of
 *  non-whitespace characters (the word a following [Ref N] marker should
 *  stay glued to) is returned separately so it can be wrapped in a nowrap
 *  span with the marker. When the fragment ends in whitespace (or is empty)
 *  there is no word to bind ⇒ trailingWord is ''. */
function splitTrailingWord(text: string): { head: string; trailingWord: string } {
  const m = text.match(/(\S+)$/);
  if (!m) return { head: text, trailingWord: '' };
  return { head: text.slice(0, m.index), trailingWord: m[1] };
}

function walkRefTokens(
  text: string,
  refs: DataSource[],
  sed?: SiteEvidenceDocument,
  docs?: SiteDocument[],
  appendixVisibleIndices?: Set<number> | null,
): React.ReactNode[] {
  // Redhead wave 3 — (defect 1) strip legacy persisted `[Ref ?]`/`[?]`
  // placeholders AND remove out-of-bounds `[Ref N]` tokens textually
  // (removed + logged — never rendered as a literal `[?]` pill, and no
  // doubled space left behind); (defect 6) repair the missing space after a
  // surviving citation token ("[Ref 2].A" → "[Ref 2]. A") before the split.
  const isTokenOob = (n: number): boolean =>
    n < 1
    || n > refs.length
    || (appendixVisibleIndices ? !appendixVisibleIndices.has(n) : false);
  const normalized = ensureSpaceAfterRefTokens(
    removeOutOfBoundsRefTokens(
      stripUnresolvableRefTokens(normalizeCompoundRefTokens(text), 'walkCitationText'),
      isTokenOob,
      'walkCitationText',
    ),
  );
  const parts = normalized.split(SPLIT_RE);
  const out: React.ReactNode[] = [];
  // Carries the word immediately preceding the NEXT token so the marker can
  // be wrapped together with it in a nowrap span (so the [Ref N] pill can
  // never start a line by itself / float mid-sentence in the narrow
  // two-column print flow — the reported defect). RRQ CITATION FIX.
  let pendingWord = '';

  parts.forEach((part, i) => {
    const m = part.match(TOKEN_RE);
    if (!m) {
      // Plain text. Only hold back the trailing word when a [Ref N] token
      // immediately FOLLOWS (so it can be glued to the marker); otherwise emit
      // the part verbatim. `split` with a capturing group alternates
      // text/token/text/…, so the next part is the binding test.
      const next = parts[i + 1];
      const tokenFollows = typeof next === 'string' && TOKEN_RE.test(next);
      if (tokenFollows) {
        const { head, trailingWord } = splitTrailingWord(part);
        if (head) out.push(head);
        pendingWord = trailingWord;
      } else {
        if (part) out.push(part);
        pendingWord = '';
      }
      return;
    }
    const n = Number(m[1]);
    // A token is out-of-bounds when it falls outside the references
    // array OR (when the caller supplies the appendix-visibility set)
    // when its appendix row was filtered out — so the in-text marker
    // matches what the reader can actually scroll to in the appendix.
    // A null/undefined set means "no appendix constraint" → fall back
    // to the plain length-bounds check (back-compat for callers that
    // don't compute appendix visibility).
    const inArray = n >= 1 && n <= refs.length;
    const inAppendix = appendixVisibleIndices ? appendixVisibleIndices.has(n) : true;
    const outOfBounds = !inArray || !inAppendix;
    const ref = refs[n - 1];
    const lineage = resolveDocumentLineage(ref, sed);
    const linkedDoc = lineage && docs ? docs.find(d => d.id === lineage.docId) : undefined;
    const refLink = (
      <RefLink
        n={n}
        reference={ref}
        outOfBounds={outOfBounds}
        linkedDocumentRef={lineage}
        linkedDocument={linkedDoc}
      />
    );
    // Glue the preceding word to the marker so the pair never breaks across a
    // line. When there is no preceding word (token starts the string) the
    // marker still renders, just without a leading word.
    out.push(
      <span key={i} style={{ whiteSpace: 'nowrap' }}>
        {pendingWord}
        {refLink}
      </span>,
    );
    pendingWord = '';
  });

  return out;
}

export function walkCitationText(
  text: string | undefined | null,
  refs: DataSource[] | undefined,
  sed?: SiteEvidenceDocument,
  docs?: SiteDocument[],
  appendixVisibleIndices?: Set<number> | null,
): React.ReactNode[] {
  if (!text) return [];
  const list = refs ?? [];
  const resolveDocUrlToRef = (rawUrl: string): string => {
    const trimmed = rawUrl.trim();
    const anchor = parseDocCitationUrl(trimmed);
    if (anchor?.docId.startsWith('sim/') || /^doc:\/\/sim\//i.test(trimmed)) return rawUrl;
    const base = trimmed.split(/[?#]/)[0]!;
    const idx = list.findIndex(
      (r) =>
        (anchor && r.documentId === anchor.docId) ||
        (typeof r.url === 'string' && (
          r.url === trimmed ||
          r.url.split(/[?#]/)[0] === base ||
          (anchor && r.url.startsWith(`doc://${anchor.docId}`))
        )),
    );
    return idx >= 0 ? ` [Ref ${idx + 1}]` : '';
  };
  const docResolved = text
    .replace(/\s*\[[^\]\n]{1,80}\]\((doc:\/\/(?!sim\/)[^)\s]*)\)/gi, (_m, url) => resolveDocUrlToRef(url))
    .replace(/\(\s*(doc:\/\/(?!sim\/)[^\s(),;]+)\s*,\s*([^()]+)\)/gi, (_m, url, rest: string) => `(${rest.trim()})${resolveDocUrlToRef(url)}`)
    .replace(/(?:,\s*|\s+)?\((doc:\/\/(?!sim\/)[^\s)\],;"']*[^\s)\],;."'])\)/gi, (_m, url) => resolveDocUrlToRef(url))
    .replace(/(?:,\s*|\s+)?(doc:\/\/(?!sim\/)[^\s)\],;"']*[^\s)\],;."'])/gi, (_m, url) => resolveDocUrlToRef(url));
  const cleaned = presentReportReliabilityTokens(stripNarrativeArtifacts(docResolved))
    .replace(/(\[Ref (\d+)\])(?:\s*,?\s*\1)+/g, '$1');
  if (!cleaned) return [];
  // A withheld narrative (reviewer-voice gate) gets its designed treatment,
  // not body prose. Inline so it is valid inside the caller's <p>.
  if (isWithheldNarrativeNotice(cleaned)) {
    return [<WithheldNarrativeNotice key="withheld-narrative" variant="inline" />];
  }
  const docNormalized = cleaned;
  if (!docNormalized) return [];
  if (DOC_SPLIT_RE.test(docNormalized)) {
    DOC_SPLIT_RE.lastIndex = 0;
    const parts = docNormalized.split(DOC_SPLIT_RE);
    const out: React.ReactNode[] = [];
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i]!;
      if (/^doc:\/\//i.test(part)) {
        out.push(<DocCitationLink key={`doc-${i}`} url={part} docs={docs} />);
      } else if (part) {
        out.push(...walkRefTokens(part, list, sed, docs, appendixVisibleIndices));
      }
    }
    return out;
  }
  return walkRefTokens(docNormalized, list, sed, docs, appendixVisibleIndices);
}
